import React, { useState, useEffect, useRef } from 'react';
import API from '../../api';
import { CalendarPlus, Edit2, Trash2, X } from 'lucide-react';

export default function HolidayManager() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({ name: '', date: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    fetchHolidays();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const res = await API.get('/holidays');
      setHolidays(res.data.holidays || []);
    } catch {
      setError('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingHoliday(null);
    setFormData({ name: '', date: '' });
    setError('');
    setSuccess('');
    setModalOpen(true);
  };

  const openEditModal = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({ name: holiday.name, date: holiday.date.slice(0, 10) });
    setError('');
    setSuccess('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setError('');
    setSuccess('');
    setSubmitting(false);
  };

  const handleInputChange = e => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    setSuccess('');
  };

const validateForm = () => {
  if (!formData.name.trim() || !formData.date) {
    setError('Please fill both fields');
    return false;
  }
  return true;
};

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (editingHoliday) {
        await API.put(`/holidays/${editingHoliday.id}`, formData);
        setSuccess('Holiday updated successfully');
      } else {
        await API.post('/holidays', formData);
        setSuccess('Holiday added successfully');
      }
      fetchHolidays();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save holiday');
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;
    try {
      await API.delete(`/holidays/${id}`);
      fetchHolidays();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete holiday');
    }
  };

  // Accessibility: trap focus inside modal
  useEffect(() => {
    if (!modalOpen) return;
    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    function handleTab(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
    document.addEventListener('keydown', handleTab);
    firstElement?.focus();
    return () => {
      document.removeEventListener('keydown', handleTab);
    };
  }, [modalOpen]);

  return (
    <div className="max-w-4xl mx-auto p-6 sm:p-10 space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-3xl font-semibold flex items-center gap-3 text-gray-900">
          <CalendarPlus className="w-7 h-7 text-blue-600" />
          Holiday Manager
        </h2>
        <button
          onClick={openAddModal}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition font-semibold shadow-md"
          aria-label="Add Holiday"
        >
          <CalendarPlus className="w-5 h-5" />
          Add Holiday
        </button>
      </div>

      <div className="bg-white shadow-lg rounded-xl overflow-x-auto border border-gray-200">
        {loading ? (
          <div className="p-10 text-center text-gray-500 text-lg font-medium">Loading holidays...</div>
        ) : holidays.length === 0 ? (
          <div className="p-10 text-center text-gray-400 text-lg font-medium">No holidays found.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                      <th className="px-8 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            SN
                        </th>
                <th className="px-8 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Holiday Name
                </th>
                <th className="px-8 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-8 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100 text-gray-900">
              {holidays.map((holiday, index) => (
                <tr key={holiday.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-8 py-5 whitespace-nowrap font-medium">{index + 1}</td>
                  <td className="px-8 py-5 whitespace-nowrap font-medium">{holiday.name}</td>
                  <td className="px-8 py-5 whitespace-nowrap">
                    {new Date(holiday.date).toLocaleDateString()}
                  </td>
                  <td className="px-8 py-5 whitespace-nowrap text-right space-x-4">
                    <button
                      onClick={() => openEditModal(holiday)}
                      className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(holiday.id)}
                      className="text-red-600 hover:text-red-800 font-medium inline-flex items-center gap-1"
                      title="Delete"
                      disabled={submitting}
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          aria-describedby="modal-desc"
        >
          <div
            ref={modalRef}
            className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-2xl relative"
          >
            <button
              onClick={closeModal}
              className="absolute top-5 right-5 text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>

            <h3 id="modal-title" className="text-2xl font-semibold mb-6">
              {editingHoliday ? 'Edit Holiday' : 'Add Holiday'}
            </h3>

            {error && (
              <div id="modal-desc" className="text-red-600 mb-4 font-medium" role="alert">
                {error}
              </div>
            )}
            {success && (
              <div className="text-green-600 mb-4 font-medium" role="status">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div>
                <label
                  htmlFor="holiday-name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Holiday Name
                </label>
                <input
                  id="holiday-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="E.g., Independence Day"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-3 focus:ring-blue-500"
                  disabled={submitting}
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="holiday-date"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Date
                </label>
                <input
                  id="holiday-date"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-3 focus:ring-blue-500"
                  disabled={submitting}
                  required
                />
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-3 text-gray-700 hover:text-gray-900 font-semibold transition"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition"
                  disabled={submitting}
                >
                  {editingHoliday ? 'Update Holiday' : 'Add Holiday'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
